import { gql } from '@apollo/client';

const CRM_ACCOUNT_FIELDS = `
    _id
    businessName
    stageKey
    stageEnteredAt
    stageSource
    ownerId
    leadSource
    tags
    status
    businessStatus
    isPogozoDemo
    wonAt
    lostAt
    lostReason
    linkedBusinessId
    linkedBusinessExists
    duplicateOfAccountId
    lastActivityAt
    createdAt
`;

export const GET_CRM_BOARD = gql`
    query GetCrmBoard($filter: CrmAccountFilterInput) {
        getCrmBoard(filter: $filter) {
            columns {
                stage {
                    key
                    name
                    order
                    color
                    isWon
                    isLost
                }
                count
                accounts {
                    ${CRM_ACCOUNT_FIELDS}
                }
            }
        }
    }
`;

export const LIST_CRM_ACCOUNTS = gql`
    query ListCrmAccounts($input: ListCrmAccountsInput!) {
        listCrmAccounts(input: $input) {
            data {
                ${CRM_ACCOUNT_FIELDS}
            }
            pagination {
                total
                hasNextPage
            }
        }
    }
`;

/**
 * The unified Businesses screen (Wave 3 Phase 1) — every CRM account (leads +
 * linked customers) with its derived `businessStatus` chip. Backed by the same
 * `listCrmAccounts` resolver as the board list, filtered/sorted server-side.
 */
export const LIST_CRM_BUSINESSES = gql`
    query ListCrmBusinesses($input: ListCrmAccountsInput!) {
        listCrmAccounts(input: $input) {
            data {
                _id
                businessName
                stageKey
                status
                businessStatus
                tags
                ownerId
                leadSource
                linkedBusinessId
                linkedBusinessExists
                lastActivityAt
                signedUpAt
                lastLoginAt
                createdAt
            }
            pagination {
                total
                hasNextPage
            }
        }
    }
`;

export const CRM_ACCOUNT_DETAIL_FIELDS = `
    ${CRM_ACCOUNT_FIELDS}
    pipelineId
    businessDetails {
        website
        email
        phone
        address
        teamSize
        runsClasses
        currentSoftware
        currentSystem
        wantsDemo
    }
    notes {
        _id
        body
        createdBy
        createdAt
        updatedAt
    }
    linkedBusiness {
        _id
        businessName
        abn
        status
        subscriptionState
        trialStartedAt
        trialEndsAt
        nextPaymentDate
        isPogozoDemo
    }
`;

export const GET_CRM_ACCOUNT = gql`
    query GetCrmAccount($id: String!) {
        getCrmAccount(id: $id) {
            ${CRM_ACCOUNT_DETAIL_FIELDS}
        }
    }
`;

/**
 * Resolve the CRM account holding a given tenant `Business`, so queues keyed on
 * `businessId` (the Deletion Requests page) can deep-link to the consolidated
 * profile page at `/crm/accounts/:accountId`.
 */
export const GET_CRM_ACCOUNT_BY_BUSINESS_ID = gql`
    query GetCrmAccountByBusinessId($businessId: String!) {
        getCrmAccountByBusinessId(businessId: $businessId) {
            _id
            businessName
            linkedBusinessId
        }
    }
`;

/**
 * Onboarding-sourced option lists for the CRM business-details form (Wave 3
 * Phase 10) — the current-system dropdown + services checkboxes read straight
 * from the onboarding questionnaire config so the two surfaces never drift.
 */
export const CRM_BUSINESS_DETAIL_OPTIONS = gql`
    query CrmBusinessDetailOptions {
        crmBusinessDetailOptions {
            currentSystem {
                value
                label
            }
            services {
                value
                label
            }
        }
    }
`;

/**
 * Business-profile view of the business-login log (POGO-13282) — "who from
 * Pogozo has entered this business", by the linked tenant `businessId`. Mirrors
 * the team-member-profile query, naming the acting team member per row.
 */
export const GET_BUSINESS_LOGIN_LOG_FOR_BUSINESS = gql`
    query ($input: ListBusinessLoginLogForBusinessInput!) {
        getBusinessLoginLogForBusiness(input: $input) {
            data {
                _id
                adminId
                adminName
                ip
                userAgent
                createdAt
            }
            pagination {
                total
                hasNextPage
            }
        }
    }
`;

export const EXPORT_CRM_BOARD = gql`
    query ExportCrmBoard($filter: CrmAccountFilterInput) {
        exportCrmBoard(filter: $filter) {
            fileUrl
        }
    }
`;

export const SUGGESTED_CRM_ACCOUNT_DUPLICATES = gql`
    query SuggestedCrmAccountDuplicates($accountId: String!) {
        suggestedCrmAccountDuplicates(accountId: $accountId) {
            ${CRM_ACCOUNT_DETAIL_FIELDS}
        }
    }
`;

/**
 * The database-wide Duplicates queue — every email-matched group, rather than
 * the per-account suggestions above. Selects the lean account fields only: the
 * queue shows a name, an email and a signed-up/lead chip per row, so pulling
 * the full detail fragment for every account in every group would be wasteful.
 */
export const LIST_CRM_DUPLICATE_GROUPS = gql`
    query ListCrmDuplicateGroups($input: ListCrmDuplicateGroupsInput!) {
        listCrmDuplicateGroups(input: $input) {
            data {
                email
                count
                accounts {
                    _id
                    businessName
                    linkedBusinessId
                    businessDetails {
                        email
                    }
                }
            }
            pagination {
                total
                hasNextPage
            }
        }
    }
`;
